/*! JointJS+ v4.1.0 - HTML5 Diagramming Framework

Copyright (c) 2024 client IO

 2024-12-04 


This Source Code Form is subject to the terms of the JointJS+ License
, v. 2.0. If a copy of the JointJS+ License was not distributed with this
file, You can obtain one at https://www.jointjs.com/license
 or from the JointJS+ archive as was distributed by client IO. See the LICENSE file.*/


import * as joint from '@joint/plus';

export class KeyboardService {

    keyboard: joint.ui.Keyboard;

    constructor() {
        this.keyboard = new joint.ui.Keyboard();
    }

    create(
        graph: joint.dia.Graph,
        clipboard: joint.ui.Clipboard,
        selection: joint.ui.Selection,
        paperScroller: joint.ui.PaperScroller,
        commandManager: joint.dia.CommandManager
    ) {
        const baseStep = 1;
        const fastStep = 30;
        const slowStep = 2;

        const moveSelected = (dx: number, dy: number) => {
            selection.collection.toArray().forEach(cell => {
                const pos = cell.position();
                cell.position(pos.x + dx, pos.y + dy);
            });
        };

        const handleArrowKey = (evt: KeyboardEvent, direction: 'left' | 'right' | 'up' | 'down') => {
            evt.preventDefault();

            let step = baseStep;
            if (evt.shiftKey) step = fastStep;
            if (evt.ctrlKey || evt.metaKey) step = slowStep;

            const delta = {
                left: [-step, 0],
                right: [step, 0],
                up: [0, -step],
                down: [0, step]
            }[direction];

            moveSelected(delta[0], delta[1]);
        };

        this.keyboard.on({

            'ctrl+c': () => {

                // Copy all selected elements and their associated links.
                clipboard.copyElements(selection.collection, graph);
            },

            'ctrl+v': () => {

                const pastedCells = clipboard.pasteCells(graph);

                const elements = pastedCells.filter(cell => cell.isElement());

                // Make sure pasted elements get selected immediately. This makes the UX better as
                // the user can immediately manipulate the pasted elements.
                selection.collection.reset(elements);
            },

            'ctrl+x shift+delete': () => {
                clipboard.cutElements(selection.collection, graph);
            },

            'delete backspace': (evt: joint.dia.Event) => {
                evt.preventDefault();
                graph.removeCells(selection.collection.toArray());
            },

            'ctrl+z': () => {
                commandManager.undo();
                selection.cancelSelection();
            },

            'ctrl+y': () => {
                commandManager.redo();
                selection.cancelSelection();
            },

            'ctrl+a': () => {
                selection.collection.reset(graph.getElements());
            },

            'ctrl+plus': (evt: joint.dia.Event) => {
                evt.preventDefault();
                paperScroller.zoom(0.2, { max: 5, grid: 0.2 });
            },

            'ctrl+minus': (evt: joint.dia.Event) => {
                evt.preventDefault();
                paperScroller.zoom(-0.2, { min: 0.2, grid: 0.2 });
            },

            'keydown:shift': (_evt: joint.dia.Event) => {
                paperScroller.setCursor('crosshair');
            },

            'keyup:shift': () => {
                paperScroller.setCursor('grab');
            },

            // Arrow keys with modifier handling
            'left': (evt: KeyboardEvent) => handleArrowKey(evt, 'left'),
            'right': (evt: KeyboardEvent) => handleArrowKey(evt, 'right'),
            'up': (evt: KeyboardEvent) => handleArrowKey(evt, 'up'),
            'down': (evt: KeyboardEvent) => handleArrowKey(evt, 'down')
        });
    }
    destroy() {
        this.keyboard.disable(); // disables all event bindings
    }
}
